import React, { useState } from 'react';
import { CheckCircle2, Heart, LoaderCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import { readApiJson } from '../lib/api';
import { parseDonationAmount } from '../utils/donationAmount';
import './PublicSeo.css';

const DONATION_AMOUNTS = [
  { pence: 300, label: '£3' },
  { pence: 500, label: '£5' },
  { pence: 1000, label: '£10' },
  { pence: 2000, label: '£20' }
];

const DonatePage = () => {
  const location = useLocation();
  const [busyAmount, setBusyAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  const [error, setError] = useState('');
  const status = new URLSearchParams(location.search).get('status');

  const startDonation = async (amount, source) => {
    setBusyAmount(source);
    setError('');
    try {
      const data = await readApiJson('/donations/checkout-session', {
        method: 'POST',
        body: JSON.stringify({
          amount,
          success_url: `${window.location.origin}/donate?status=success`,
          cancel_url: `${window.location.origin}/donate?status=cancelled`
        })
      });
      window.location.assign(data.url);
    } catch (checkoutError) {
      setError(checkoutError.message || 'Donation checkout could not be started.');
      setBusyAmount(null);
    }
  };

  const submitCustomAmount = (event) => {
    event.preventDefault();
    const parsed = parseDonationAmount(customAmount);
    if (parsed.error) {
      setAmountError(parsed.error);
      return;
    }
    setAmountError('');
    startDonation(parsed.amount, 'custom');
  };

  return (
    <main className="public-seo-page">
      <SeoMeta
        title="Support CBAT Academy | Donate"
        description="Make a voluntary one-time contribution toward the development and maintenance of CBAT Academy."
        path="/donate"
        robots="noindex,follow"
      />
      <section className="public-seo-hero public-seo-hero--compact">
        <p className="public-kicker">Support the project</p>
        <h1>Help keep CBAT Academy improving</h1>
        <p className="public-seo-lede">
          CBAT Academy is independently built and maintained. A voluntary contribution helps cover hosting, development and the time needed to test, document and improve the practice modules.
        </p>
      </section>

      <article className="public-seo-copy">
        {status === 'success' && (
          <div className="mb-8 flex items-start gap-3 rounded-md border border-[#285b44] bg-[#0b251d] p-4 text-[#8ce9bd]" role="status">
            <CheckCircle2 className="mt-0.5 shrink-0" size={20} aria-hidden="true" />
            <div><strong>Thank you for supporting CBAT Academy.</strong><br />Your payment was completed through Stripe.</div>
          </div>
        )}
        {status === 'cancelled' && (
          <div className="mb-8 rounded-md border border-[#3b5067] bg-[#091325] p-4 text-[#9db1c5]" role="status">
            Checkout was cancelled and no contribution was made.
          </div>
        )}

        <h2>Choose a one-time amount</h2>
        <p>Choose a quick amount or enter your own. Contributions are optional, do not create a subscription and do not unlock features or affect training results.</p>

        <div className="donation-grid" aria-label="Donation amount">
          {DONATION_AMOUNTS.map((amount) => (
            <button
              key={amount.pence}
              type="button"
              disabled={busyAmount !== null}
              onClick={() => {
                setAmountError('');
                startDonation(amount.pence, `preset-${amount.pence}`);
              }}
              className="donation-option"
            >
              {busyAmount === `preset-${amount.pence}` ? <LoaderCircle className="animate-spin" size={20} aria-hidden="true" /> : <Heart size={19} aria-hidden="true" />}
              <span>{amount.label}</span>
            </button>
          ))}
        </div>

        <form className="donation-custom" onSubmit={submitCustomAmount}>
          <div className="donation-custom__copy">
            <label htmlFor="custom-donation-amount">Other amount</label>
            <span id="custom-donation-hint">Between £1 and £500</span>
          </div>
          <div className="donation-custom__controls">
            <div className="donation-custom__field">
              <span aria-hidden="true">£</span>
              <input
                id="custom-donation-amount"
                type="number"
                inputMode="decimal"
                min="1"
                max="500"
                step="0.01"
                value={customAmount}
                onChange={(event) => {
                  setCustomAmount(event.target.value);
                  setAmountError('');
                }}
                aria-describedby="custom-donation-hint custom-donation-error"
                placeholder="25.00"
                disabled={busyAmount !== null}
                required
              />
            </div>
            <button type="submit" disabled={busyAmount !== null}>
              {busyAmount === 'custom' ? <LoaderCircle className="animate-spin" size={17} aria-hidden="true" /> : <Heart size={17} aria-hidden="true" />}
              Continue
            </button>
          </div>
          <span id="custom-donation-error" className="donation-custom__error" role={amountError ? 'alert' : undefined}>{amountError}</span>
        </form>

        {error && <p className="mt-4 text-sm text-[#ffb3bb]" role="alert">{error}</p>}

        <h2>How contributions are used</h2>
        <ul>
          <li>Hosting, storage and service costs.</li>
          <li>Testing and maintaining interactive practice modules.</li>
          <li>Writing and reviewing practical guidance.</li>
          <li>Fixing accessibility, compatibility and reliability issues.</li>
        </ul>

        <p className="public-guide-disclaimer">
          Payments are processed securely by Stripe. CBAT Academy does not receive your full card details. Contributions are voluntary payments to support the project and should not be treated as charitable donations for tax purposes.
        </p>

        <div className="public-actions">
          <Link className="public-button public-button--secondary" to="/about">About the project</Link>
          <Link className="public-button public-button--secondary" to="/privacy">Privacy information</Link>
        </div>
      </article>
    </main>
  );
};

export default DonatePage;
